import { TOURNAMENT_SYSTEM_BADGE_CLASS_MAP } from '@/config';
import {
  ChessVariantLabel,
  CompetitionFormat,
  ControlType,
  TeamMode,
  TournamentStatus,
  TournamentSystemLabel,
} from '@/services/utils/enums';
import { formatDate } from '@/services/utils/dateFormat';
import IconChessVariant from '@/components/layout/icons/ChessVariant/ChessVariant.vue';
import IconBullet from '@/components/layout/icons/Bullet/Bullet.vue';
import IconBlitz from '@/components/layout/icons/Blitz/Blitz.vue';
import IconRapid from '@/components/layout/icons/Rapid/Rapid.vue';
import IconClassic from '@/components/layout/icons/Classic/Classic.vue';

export default {
  name: 'PublicSearchResultCard',
  components: {
    IconChessVariant,
    IconBullet,
    IconBlitz,
    IconRapid,
    IconClassic,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    cardClass() {
      return {
        'public-search-result-card--compact': this.compact,
      };
    },

    routeTarget() {
      return this.item?.route || { name: 'Home' };
    },

    isTournamentResult() {
      return this.item?.type === 'event';
    },

    resolvedTypeLabel() {
      const keys = {
        event: 'search.tournament',
        club: 'search.club',
        city: 'search.city',
      };
      const key = this.item?.typeLabelKey || keys[this.item?.type];
      return key ? this.$t(key) : (this.item?.typeLabel || this.$t('search.result'));
    },

    resolvedMeta() {
      return this.item?.metaKey ? this.$t(this.item.metaKey) : this.item?.meta;
    },

    resolvedDetail() {
      if (this.item?.type === 'club' && this.item?.tournamentsCount !== undefined) {
        return this.$t('search.clubStats', {
          tournaments: this.item.tournamentsCount,
          players: this.item.playersCount || 0,
        });
      }
      if (this.item?.type === 'city' && this.item?.clubsCount !== undefined) {
        return this.$t('search.cityStats', {
          clubs: this.item.clubsCount,
          tournaments: this.item.tournamentsCount || 0,
        });
      }
      return this.item?.detail;
    },

    resolvedAction() {
      const keys = {
        event: 'search.openTournament',
        club: 'search.openClub',
        city: 'search.viewCity',
      };
      const key = this.item?.actionKey || keys[this.item?.type];
      return key ? this.$t(key) : (this.item?.action || this.$t('search.open'));
    },

    tournament() {
      return this.item?.tournament || {};
    },

    tournamentSubtitle() {
      const clubName = this.item?.clubName || '';
      const clubLocation = this.item?.clubLocation || '';
      return [clubName, clubLocation].filter(Boolean).join(' · ') || this.item?.meta || '';
    },

    tournamentStatus() {
      const status = this.tournament?.status;

      if (status === TournamentStatus.ONGOING) {
        return { icon: 'bi bi-play-circle', label: this.$t('tournamentStatuses.ongoing'), className: 'public-status-badge--ongoing public-badge--compact' };
      }

      if (status === TournamentStatus.FINISHED) {
        return { icon: 'bi bi-flag-fill', label: this.$t('tournamentStatuses.finished'), className: 'public-status-badge--finished public-badge--compact' };
      }

      return { icon: 'bi bi-hourglass-split', label: this.$t('tournamentStatuses.created'), className: 'public-status-badge--created public-badge--compact' };
    },

    tournamentSystemLabel() {
      const system = this.tournament?.system;
      const key = `search.systemLabels.${system}`;
      return system && this.$te(key)
        ? this.$t(key)
        : (TournamentSystemLabel[system] || system || this.$t('search.system'));
    },

    tournamentSystemClass() {
      return TOURNAMENT_SYSTEM_BADGE_CLASS_MAP[this.tournament?.system] || '';
    },

    tournamentDateBadge() {
      const date = this.tournament?.date;
      if (!date) return '';

      if (/^\d{2}\.\d{2}\.\d{4}$/.test(date)) return date;
      return formatDate(date, 'dd-mm-yyyy');
    },

    tournamentDateBadgeClass() {
      return 'public-date-badge--muted public-badge--compact';
    },

    tournamentControlLabel() {
      const control = this.tournament?.control || {};
      if (control.label) return control.label;

      const labels = {
        [ControlType.BULLET]: 'Bullet',
        [ControlType.BLITZ]: 'Blitz',
        [ControlType.RAPID]: 'Rapid',
        [ControlType.CLASSIC]: 'Classic',
      };
      const label = labels[control.type] || this.$t('search.control');
      return control.timeMinutes == null ? label : `${label} ${control.timeMinutes}+${control.increment ?? 0}`;
    },

    tournamentControlIcon() {
      const icons = {
        [ControlType.BULLET]: 'IconBullet',
        [ControlType.BLITZ]: 'IconBlitz',
        [ControlType.RAPID]: 'IconRapid',
        [ControlType.CLASSIC]: 'IconClassic',
      };
      return icons[this.tournament?.control?.type] || null;
    },

    tournamentChessVariantLabel() {
      const variant = this.tournament?.chessVariant;
      const key = `chessVariants.${variant}`;
      return variant && this.$te(key)
        ? this.$t(key)
        : (ChessVariantLabel[variant] || variant || '—');
    },

    tournamentRatedLabel() {
      return this.$t(this.tournament?.rated ? 'search.ratedShort' : 'search.friendlyShort');
    },

    tournamentRatedIcon() {
      return this.tournament?.rated ? 'bi bi-patch-check' : 'bi bi-cup-hot';
    },

    tournamentRatedIconColor() {
      return this.tournament?.rated ? 'var(--tournament-rating-rated)' : 'var(--tournament-rating-friendly)';
    },

    tournamentFormatLabel() {
      return this.$t(this.tournament?.competitionFormat === CompetitionFormat.TEAM ? 'search.team' : 'search.individual');
    },

    tournamentFormatIcon() {
      return this.tournament?.competitionFormat === CompetitionFormat.TEAM ? 'bi bi-people-fill' : 'bi bi-person-fill';
    },

    tournamentFormatIconColor() {
      return this.tournament?.competitionFormat === CompetitionFormat.TEAM
        ? 'var(--tournament-format-team)'
        : 'var(--tournament-format-individual)';
    },

    tournamentTeamMode() {
      if (this.tournament?.competitionFormat !== CompetitionFormat.TEAM) return null;
      return this.tournament?.teamMode === TeamMode.FIXED
        ? { label: this.$t('search.fixed'), icon: 'bi bi-pin-angle-fill' }
        : { label: this.$t('search.random'), icon: 'bi bi-dice-5' };
    },
  },
};
